import type { CalendarUnit } from '../due.js';
import { DomainError } from '../errors.js';
import type { LocalDate } from '../local-date.js';
import type { EquipmentStatus } from './equipment.js';
import { iso14224ActivityOf, type JobInterval } from './jobs.js';

/**
 * DNV MAD (Maintenance Activity Data) aktarımı (ADR 0132; ortak 2026-10-28). DNV'nin
 * Machinery Maintenance Connect (MMC) panosu bakım kayıtlarını bu biçimde alır; DNV-CP-0206
 * Sec.2 2'ye göre PMS tip onayında sınanır. Kaynaklar `docs/gereksinimler/ortak-2026-10-28/dnv-mad/`:
 * `mmc_schema.json` (makine sözleşmesi; doğrulama ve kısaltma buna göre), `MAD_datafields.xlsx`
 * (Interface Description; tablo sütun sırası), `mad.yaml` (API). Saf modül: I/O yok.
 */

/** Gönderen sistemin adı (`source`). */
export const MAD_SOURCE = 'Felixmate';

/** DNV'nin canlı uç adresi (mad.yaml `servers[0]` + `/invoke`). */
export const MAD_DEFAULT_ENDPOINT =
  'https://api.veracity.com/ext/dnvgl/mmc/v1/mmc_upload_file/invoke';

/** Bir çağrıdaki en çok iş: DNV 100–100 000 önerir (mad.yaml). */
export const MAD_MAX_TASKS_PER_CALL = 100_000;

/** Varsayılan grup boyutu (ADR 0132 §5): gövde ~10 MB altında kalsın. */
export const MAD_DEFAULT_BATCH = 10_000;

type FieldType = 'string' | 'integer' | 'date';
export interface MadFieldSpec {
  type: FieldType;
  maxLength?: number;
  minimum?: number;
  maximum?: number;
  enum?: readonly string[];
}

/** `mmc_schema.json` `tasks.items.properties`: tür, uzunluk, aralık ve enum (sıra şemadaki). */
const TASK_SCHEMA = {
  imonumber: { type: 'integer', minimum: 0, maximum: 9_999_999 },
  vesselname: { type: 'string', maxLength: 64 },
  classcomponent: { type: 'string', maxLength: 64 },
  componentcode: { type: 'string', maxLength: 64 },
  componentname: { type: 'string', maxLength: 256 },
  componenttype: { type: 'string', maxLength: 64, enum: ['MACH', 'DRILL', 'PROD'] },
  componentcategory: { type: 'string', maxLength: 64, enum: ['STAT', 'N-STAT'] },
  jobtype: { type: 'string', maxLength: 64 },
  jobname: { type: 'string', maxLength: 64 },
  jobno: { type: 'string', maxLength: 64 },
  jobrevno: { type: 'string', maxLength: 64 },
  jobdescription: { type: 'string', maxLength: 1024 },
  localjobdescription: { type: 'string', maxLength: 1024 },
  criticality: { type: 'string', maxLength: 64 },
  workhours: { type: 'integer', minimum: 0 },
  duehours: { type: 'integer', minimum: 0 },
  donehours: { type: 'integer', minimum: 0 },
  duedate: { type: 'date' },
  donedate: { type: 'date' },
  interval: { type: 'string', maxLength: 64 },
  intervaltype: { type: 'string', maxLength: 64 },
  postponed: { type: 'string', maxLength: 64 },
  postponedhours: { type: 'integer', minimum: 0 },
  postponeddate: { type: 'date' },
  deferred: { type: 'string', maxLength: 64, enum: ['YES', 'NO'] },
  measurement: { type: 'string', maxLength: 256 },
  barrier: { type: 'string', maxLength: 64 },
  department: { type: 'string', maxLength: 64 },
  reporting: { type: 'string', maxLength: 1024 },
  remarks: { type: 'string', maxLength: 1024 },
  reason: { type: 'string', maxLength: 1024 },
  symptom: { type: 'string', maxLength: 1024 },
  conditionbefore: { type: 'string', maxLength: 64 },
  conditionafter: { type: 'string', maxLength: 64 },
  solas: { type: 'string', maxLength: 64 },
  pmod: { type: 'string', maxLength: 128 },
} as const satisfies Record<string, MadFieldSpec>;
export type MadTaskField = keyof typeof TASK_SCHEMA;
export const MAD_TASK_SCHEMA: Readonly<Record<MadTaskField, MadFieldSpec>> = TASK_SCHEMA;
export const MAD_TASK_FIELDS = Object.keys(MAD_TASK_SCHEMA) as MadTaskField[];

/** Şemada `required` olan 28 alan: değeri yoksa boş metin ya da 0 gönderilir. */
export const MAD_REQUIRED_FIELDS = [
  'imonumber',
  'vesselname',
  'classcomponent',
  'componentcode',
  'componentname',
  'jobtype',
  'jobname',
  'jobno',
  'jobdescription',
  'localjobdescription',
  'criticality',
  'workhours',
  'duehours',
  'donehours',
  'duedate',
  'donedate',
  'interval',
  'intervaltype',
  'postponed',
  'postponedhours',
  'postponeddate',
  'department',
  'reporting',
  'remarks',
  'reason',
  'symptom',
  'conditionbefore',
  'conditionafter',
] as const satisfies readonly MadTaskField[];

/**
 * Interface Description'ın (`MAD_datafields.xlsx`) sütun sırası: Excel/CSV indirmesi ve DNV'nin
 * "set template" yüklemesi bu sırayla. İlk sütun gövdenin `company` alanıdır.
 */
export const MAD_INTERFACE_COLUMNS = [
  'company',
  'imonumber',
  'vesselname',
  'classcomponent',
  'componentcode',
  'componentname',
  'jobtype',
  'jobname',
  'jobno',
  'jobdescription',
  'criticality',
  'donehours',
  'duehours',
  'duedate',
  'donedate',
  'interval',
  'intervaltype',
  'postponed',
  'postponedhours',
  'postponeddate',
  'reporting',
  'reason',
  'localjobdescription',
  'workhours',
  'department',
  'remarks',
  'symptom',
  'conditionbefore',
  'conditionafter',
  'solas',
  'pmod',
] as const;

/** Bir MAD işi: 28 zorunlu alan ve gönderdiğimiz isteğe bağlılar (`solas`, `pmod`). */
export interface MadTask {
  imonumber: number;
  vesselname: string;
  classcomponent: string;
  componentcode: string;
  componentname: string;
  jobtype: string;
  jobname: string;
  jobno: string;
  jobdescription: string;
  localjobdescription: string;
  criticality: string;
  workhours: number;
  duehours: number;
  donehours: number;
  duedate: string;
  donedate: string;
  interval: string;
  intervaltype: string;
  postponed: string;
  postponedhours: number;
  postponeddate: string;
  department: string;
  reporting: string;
  remarks: string;
  reason: string;
  symptom: string;
  conditionbefore: string;
  conditionafter: string;
  solas: string;
  pmod?: string;
  /** Ertelenmiş işte: klas kabullü erteleme YES, değilse NO (ADR 0133 §5); ertelenmemiş işte yok. */
  deferred?: 'YES' | 'NO';
}

export interface MadBody {
  company: string;
  source: string;
  created: string;
  tasks: MadTask[];
}

export type MadIssueSeverity = 'error' | 'warning';
export interface MadIssue {
  /** Alan; gövde düzeyinde `company`, `source`, `created`, `tasks`. */
  field: string | null;
  code: string;
  severity: MadIssueSeverity;
}

/** Eşlemenin girdisi: iptal edilmemiş bir iş kaydı ve bağlamı (ofiste derlenir). */
export interface MadSourceRecord {
  recordId: string;
  imo: string | null;
  vesselName: string;
  equipment: {
    code: string;
    sfiCode: string | null;
    name: string;
    /** Etkin kritiklik: kendisi ya da atası [NR496 4.4]. */
    critical: boolean;
    classRelevant: boolean;
  };
  /** Ofisin eşlemesi (ADR 0132 §1): DNV klas bileşen kodu ve VIS kodu. */
  dnv: { classComponent: string | null; visCode: string | null };
  /** İş tanımı; plansız iş emrinde yok. */
  job: {
    code: string | null;
    title: string | null;
    workType: string | null;
    jobType: string | null;
    procedure: readonly string[];
  } | null;
  workOrder: { woNo: string | null; title: string | null; kind: string | null };
  /** Kaydın tamamlandığı andaki etkin aralık (aralık değişiklikleri işlenmiş). */
  interval: JobInterval | null;
  /** Sorumlu rol (DNV `department`). */
  department: string | null;
  doneOn: LocalDate;
  runningHours: number | null;
  dueOn: LocalDate | null;
  dueHours: number | null;
  workHours: number | null;
  workPerformed: string;
  remarks: string | null;
  overdueReason: string | null;
  conditionBefore: EquipmentStatus | string | null;
  conditionAfter: EquipmentStatus | string | null;
  /** İş emrinin bağlı arızasının tanımı (DNV `symptom`). */
  symptom: string | null;
  /** Kaydın döngüsünün onaylı ertelemeleri: son yeni vade ve gerekçeler. */
  postponement: {
    newDueOn: LocalDate | null;
    newDueHours: number | null;
    reasons: readonly string[];
    /**
     * Ertelemelerden birinin klas referansı (klas onayı / kabulü); varsa erteleme "deferred"
     * sayılır (DNV-CP-0206 Sec.2 [4.3] g; ADR 0133 §5). Eski çağıranlar vermez.
     */
    classReference?: string | null;
  } | null;
}

const CONDITION_TEXT: Readonly<Record<string, string>> = {
  operational: 'GOOD',
  operational_with_restrictions: 'ACCEPTABLE',
  not_operational: 'BAD',
};

/** Ekipman durumu → DNV metni (mad.yaml "ex GOOD, BAD"). */
export function madConditionText(status: string | null | undefined): string {
  if (status === null || status === undefined || status === '') return '';
  return CONDITION_TEXT[status] ?? status.toUpperCase();
}

const UNIT_TEXT: Readonly<Record<CalendarUnit, [string, string]>> = {
  day: ['Day', 'Days'],
  week: ['Week', 'Weeks'],
  month: ['Month', 'Months'],
  year: ['Year', 'Years'],
};

/**
 * Aralık metni ve türü (Interface Description "CAL / RUN / MIX / COR"): takvim CAL, çalışma saati
 * RUN, ikisi MIX, yalnız durum noktası (durum bazlı) COR. Aralıksız iş (plansız) boş.
 */
export function madIntervalOf(interval: JobInterval | null): {
  interval: string;
  intervaltype: string;
} {
  if (!interval) return { interval: '', intervaltype: '' };
  const calendar = interval.calendar
    ? `${interval.calendar.every} ${UNIT_TEXT[interval.calendar.unit][interval.calendar.every === 1 ? 0 : 1]}`
    : null;
  const hours = interval.runningHours !== null ? `${interval.runningHours} Hrs` : null;
  if (calendar && hours) return { interval: `${calendar} / ${hours}`, intervaltype: 'MIX' };
  if (calendar) return { interval: calendar, intervaltype: 'CAL' };
  if (hours) return { interval: hours, intervaltype: 'RUN' };
  if (interval.conditionPointIds.length > 0) {
    return { interval: 'Condition based', intervaltype: 'COR' };
  }
  return { interval: '', intervaltype: '' };
}

const JOB_TYPE_TEXT: Readonly<Record<string, string>> = {
  preventive: 'PREVENTIVE',
  inspection: 'INSPECTION',
  test: 'TEST',
  condition_based: 'CONDITION',
  class_survey: 'CLASS SURVEY',
};

/**
 * İş türü (mad.yaml "ex TEST, CLEAN, INSPECTION, OVERHAUL"): işin ISO 14224 Table B.5 etkinliği
 * (DNV-CP-0206 Sec.2 [4.3] f; ADR 0133 §4) büyük harfle; tanınmayan eski tür olduğu gibi. Türsüz
 * işte sınıfı; görevsiz düzeltici iş emrinde (unplanned, corrective, breakdown) "REPAIR".
 */
function jobTypeOf(source: MadSourceRecord): string {
  const work = source.job?.workType?.trim();
  if (work) return (iso14224ActivityOf(work) ?? work).toUpperCase();
  const type = source.job?.jobType?.trim();
  if (type) return JOB_TYPE_TEXT[type] ?? type.toUpperCase();
  return source.workOrder.kind && source.workOrder.kind !== 'planned'
    ? 'REPAIR'
    : (source.workOrder.kind ?? '').toUpperCase();
}

const textOf = (value: string | null | undefined) => (value ?? '').trim();
const wholeHours = (value: number | null | undefined) =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.round(value) : 0;

/** 7 haneli IMO numarası; "IMO 9876543" gibi önekler atılır. Geçersizse `null`. */
export function madImoNumber(imo: string | null | undefined): number | null {
  const digits = (imo ?? '').replace(/^\s*IMO\s*/i, '').trim();
  return /^\d{7}$/.test(digits) ? Number(digits) : null;
}

/**
 * İş kaydı → MAD işi (ADR 0132 §3; README eşleme tablosu). Değeri olmayan zorunlu alan boş metin
 * ya da 0; vadesiz işte `duedate` yapılış günü (gecikme sayılmaz); ertelenmemiş işte
 * `postponeddate` yapılış günü (mad.yaml). Uzun metin şemanın sınırında kısalır, uyarı olur.
 * IMO yoksa hata (DNV'nin birleştirme anahtarı); klas ilgili ekipmanda DNV kodu yoksa uyarı.
 */
export function madTaskOf(source: MadSourceRecord): { task: MadTask; issues: MadIssue[] } {
  const issues: MadIssue[] = [];
  const imo = madImoNumber(source.imo);
  if (imo === null) issues.push({ field: 'imonumber', code: 'IMO_MISSING', severity: 'error' });
  const classComponent = textOf(source.dnv.classComponent);
  if (source.equipment.classRelevant && classComponent === '') {
    issues.push({ field: 'classcomponent', code: 'CLASS_COMPONENT_MISSING', severity: 'warning' });
  }
  const jobName = textOf(source.job?.title) || textOf(source.workOrder.title);
  const jobNo = textOf(source.job?.code) || textOf(source.workOrder.woNo);
  if (jobNo === '') issues.push({ field: 'jobno', code: 'JOB_NO_MISSING', severity: 'error' });
  const procedure = (source.job?.procedure ?? []).map((s) => s.trim()).filter((s) => s !== '');
  const { interval, intervaltype } = madIntervalOf(source.interval);
  const postponed = source.postponement !== null;
  const reasons = (source.postponement?.reasons ?? []).map((r) => r.trim()).filter(Boolean);
  const pmod = textOf(source.dnv.visCode);
  const task: MadTask = {
    imonumber: imo ?? 0,
    vesselname: textOf(source.vesselName),
    classcomponent: classComponent,
    componentcode: textOf(source.equipment.sfiCode) || textOf(source.equipment.code),
    componentname: textOf(source.equipment.name),
    jobtype: jobTypeOf(source),
    jobname: jobName,
    jobno: jobNo,
    jobdescription: procedure.length > 0 ? procedure.join('; ') : jobName,
    localjobdescription: '',
    criticality: source.equipment.critical ? 'YES' : 'NO',
    workhours: wholeHours(source.workHours),
    duehours: wholeHours(source.dueHours),
    donehours: wholeHours(source.runningHours),
    duedate: source.dueOn ?? source.doneOn,
    donedate: source.doneOn,
    interval,
    intervaltype,
    postponed: postponed ? 'YES' : 'NO',
    postponedhours: postponed ? wholeHours(source.postponement?.newDueHours) : 0,
    postponeddate: (postponed ? source.postponement?.newDueOn : null) ?? source.doneOn,
    department: textOf(source.department),
    reporting: textOf(source.workPerformed),
    remarks: textOf(source.remarks),
    // "Reason for postponement" (Interface Description); ertelenmemiş gecikmiş işte gecikme gerekçesi
    reason: reasons.length > 0 ? reasons.join('; ') : textOf(source.overdueReason),
    symptom: textOf(source.symptom),
    conditionbefore: madConditionText(source.conditionBefore),
    conditionafter: madConditionText(source.conditionAfter),
    solas: '',
    ...(pmod !== '' ? { pmod } : {}),
    ...(postponed
      ? { deferred: textOf(source.postponement?.classReference) !== '' ? 'YES' : 'NO' }
      : {}),
  };
  const record = task as unknown as Record<string, unknown>;
  for (const field of MAD_TASK_FIELDS) {
    const spec = MAD_TASK_SCHEMA[field];
    const value = record[field];
    if (typeof value !== 'string' || spec.maxLength === undefined) continue;
    const chars = Array.from(value);
    if (chars.length > spec.maxLength) {
      record[field] = chars.slice(0, spec.maxLength).join('');
      issues.push({ field, code: 'TRUNCATED', severity: 'warning' });
    }
  }
  return { task, issues };
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const validDate = (value: string) =>
  DATE.test(value) && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;

/** Bir işin `mmc_schema.json`'a göre doğrulanması: zorunluluk, tür, uzunluk, aralık, tarih, enum. */
export function validateMadTask(task: Readonly<Record<string, unknown>> | MadTask): MadIssue[] {
  const record = task as Readonly<Record<string, unknown>>;
  const issues: MadIssue[] = [];
  const error = (field: string, code: string) => issues.push({ field, code, severity: 'error' });
  for (const field of MAD_REQUIRED_FIELDS) {
    if (record[field] === undefined || record[field] === null) error(field, 'REQUIRED');
  }
  for (const field of MAD_TASK_FIELDS) {
    const value = record[field];
    if (value === undefined || value === null) continue;
    const spec = MAD_TASK_SCHEMA[field];
    if (spec.type === 'integer') {
      if (typeof value !== 'number' || !Number.isInteger(value)) {
        error(field, 'INVALID_TYPE');
      } else if (
        (spec.minimum !== undefined && value < spec.minimum) ||
        (spec.maximum !== undefined && value > spec.maximum)
      ) {
        error(field, 'OUT_OF_RANGE');
      }
      continue;
    }
    if (typeof value !== 'string') {
      error(field, 'INVALID_TYPE');
      continue;
    }
    if (spec.type === 'date') {
      if (!validDate(value)) error(field, 'INVALID_DATE');
      continue;
    }
    if (spec.maxLength !== undefined && Array.from(value).length > spec.maxLength) {
      error(field, 'TOO_LONG');
    }
    if (spec.enum && !spec.enum.includes(value)) error(field, 'INVALID_VALUE');
  }
  return issues;
}

/** Gövdenin ve her işin doğrulanması (`mmc_schema.json`). */
export function validateMadBody(input: Readonly<Record<string, unknown>> | MadBody): {
  body: MadIssue[];
  tasks: { index: number; issues: MadIssue[] }[];
} {
  const body = input as Readonly<Record<string, unknown>>;
  const issues: MadIssue[] = [];
  for (const field of ['company', 'source'] as const) {
    if (typeof body[field] !== 'string' || (body[field] as string).trim() === '') {
      issues.push({ field, code: 'REQUIRED', severity: 'error' });
    }
  }
  if (body.created === undefined || body.created === null) {
    issues.push({ field: 'created', code: 'REQUIRED', severity: 'error' });
  } else if (typeof body.created !== 'string' || !validDate(body.created)) {
    issues.push({ field: 'created', code: 'INVALID_DATE', severity: 'error' });
  }
  if (!Array.isArray(body.tasks)) {
    issues.push({ field: 'tasks', code: 'REQUIRED', severity: 'error' });
    return { body: issues, tasks: [] };
  }
  return {
    body: issues,
    tasks: (body.tasks as Record<string, unknown>[]).map((task, index) => ({
      index,
      issues: validateMadTask(task),
    })),
  };
}

export function madBodyOf(input: {
  company: string;
  created: LocalDate;
  tasks: MadTask[];
  source?: string;
}): MadBody {
  return {
    company: input.company,
    source: input.source ?? MAD_SOURCE,
    created: input.created,
    tasks: input.tasks,
  };
}

/** İşleri en çok `size`'lık gruplara böler (≤ 100 000; mad.yaml). */
export function madChunks<T>(items: readonly T[], size: number): T[][] {
  if (!Number.isInteger(size) || size < 1 || size > MAD_MAX_TASKS_PER_CALL) {
    throw new DomainError('INVALID_BATCH_SIZE', String(size));
  }
  const result: T[][] = [];
  for (let i = 0; i < items.length; i += size) result.push(items.slice(i, i + size));
  return result;
}

/** Excel/CSV satırları: başlık Interface Description sırası, her satırda `company`. */
export function madTableRows(body: MadBody): (string | number)[][] {
  return [
    [...MAD_INTERFACE_COLUMNS],
    ...body.tasks.map((task) =>
      MAD_INTERFACE_COLUMNS.map((column) =>
        column === 'company' ? body.company : (task[column as keyof MadTask] ?? ''),
      ),
    ),
  ];
}

/* ---------- Eşleme dosyası (ADR 0132 §4) ---------- */

/** DNV kodu: harf, rakam ve `. _ / -`; boşluksuz (ör. MEPDIE, 411.1/C101). */
export const MAD_CODE_PATTERN = /^[A-Za-z0-9._/-]+$/;

/** Eşleme dosyasının sütunları: Mapping ekranının indirdiği dosyayla aynı. */
export const MAD_MAPPING_COLUMNS = [
  'Equipment Code',
  'Equipment Name',
  'SFI Code',
  'Class Relevant',
  'DNV Class Component',
  'DNV VIS Code',
] as const;

export interface MadMappingRow {
  /** Dosyadaki satır numarası (başlık 1). */
  row: number;
  equipmentCode: string;
  /** Sütun dosyada yoksa `undefined` (değişmez); boş hücre `null` (kod kaldırılır). */
  classComponent?: string | null;
  visCode?: string | null;
}
export interface MadMappingRowError {
  row: number;
  column: string | null;
  code: string;
}

type MappingField = 'equipmentCode' | 'classComponent' | 'visCode';

const headerKey = (value: unknown) =>
  String(value ?? '')
    .normalize('NFC')
    .trim()
    .toLocaleLowerCase('en')
    .replace(/[\s\-_/]+/g, '');

const MAPPING_ALIASES: Readonly<Record<string, MappingField>> = {
  equipmentcode: 'equipmentCode',
  code: 'equipmentCode',
  dnvclasscomponent: 'classComponent',
  classcomponent: 'classComponent',
  dnvcode: 'classComponent',
  dnviscode: 'visCode',
  dnvviscode: 'visCode',
  viscode: 'visCode',
  pmod: 'visCode',
};

/**
 * Eşleme dosyasının satırları (CSV ya da XLSX'in ilk sayfası): ilk satır başlık, sütun sırası
 * serbest; `Equipment Code` zorunlu, DNV sütunlarından en az biri. Hatalı satır uygulanmaz;
 * dosyada tekrar eden ekipman `DUPLICATE_EQUIPMENT`. Boş satırlar atlanır.
 */
export function parseMadMappingRows(rows: readonly (readonly unknown[])[]): {
  items: MadMappingRow[];
  errors: MadMappingRowError[];
} {
  const [header, ...body] = rows;
  if (!header) return { items: [], errors: [{ row: 1, column: null, code: 'EMPTY_FILE' }] };
  const positions = new Map<MappingField, number>();
  header.forEach((cell, index) => {
    const field = MAPPING_ALIASES[headerKey(cell)];
    if (field && !positions.has(field)) positions.set(field, index);
  });
  if (!positions.has('equipmentCode')) {
    return { items: [], errors: [{ row: 1, column: 'Equipment Code', code: 'MISSING_COLUMN' }] };
  }
  if (!positions.has('classComponent') && !positions.has('visCode')) {
    return {
      items: [],
      errors: [{ row: 1, column: 'DNV Class Component', code: 'MISSING_COLUMN' }],
    };
  }
  const items: MadMappingRow[] = [];
  const errors: MadMappingRowError[] = [];
  const seen = new Set<string>();
  body.forEach((cells, index) => {
    const row = index + 2;
    if (cells.every((cell) => String(cell ?? '').trim() === '')) return;
    const cell = (field: MappingField) => {
      const at = positions.get(field);
      return at === undefined ? undefined : String(cells[at] ?? '').trim();
    };
    const equipmentCode = cell('equipmentCode') ?? '';
    const rowErrors: MadMappingRowError[] = [];
    if (equipmentCode === '') {
      rowErrors.push({ row, column: 'Equipment Code', code: 'REQUIRED' });
    } else if (seen.has(equipmentCode.toLocaleLowerCase('en'))) {
      rowErrors.push({ row, column: 'Equipment Code', code: 'DUPLICATE_EQUIPMENT' });
    }
    const code = (field: Exclude<MappingField, 'equipmentCode'>, column: string, max: number) => {
      const value = cell(field);
      if (value === undefined) return undefined;
      if (value === '') return null;
      if (value.length > max || !MAD_CODE_PATTERN.test(value)) {
        rowErrors.push({ row, column, code: 'INVALID_CODE' });
      }
      return value;
    };
    const classComponent = code('classComponent', 'DNV Class Component', 64);
    const visCode = code('visCode', 'DNV VIS Code', 128);
    if (rowErrors.length > 0) {
      errors.push(...rowErrors);
      return;
    }
    seen.add(equipmentCode.toLocaleLowerCase('en'));
    items.push({
      row,
      equipmentCode,
      ...(classComponent !== undefined ? { classComponent } : {}),
      ...(visCode !== undefined ? { visCode } : {}),
    });
  });
  return { items, errors };
}
